import * as DialogPrimitive from "@radix-ui/react-dialog";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { Button } from "../components/Button";
import { ScrollArea } from "../components/ScrollArea";
import { Separator } from "../components/Separator";
import { XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;
const DialogPortal = DialogPrimitive.Portal;
const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (React.createElement(DialogPrimitive.Overlay, { className: cn("s-fixed s-inset-0 s-z-50 data-[state=open]:s-animate-in data-[state=closed]:s-animate-out data-[state=closed]:s-fade-out-0 data-[state=open]:s-fade-in-0", "s-bg-muted-foreground/75 dark:s-bg-muted-background-night/75", className), ...props, ref: ref })));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DIALOG_SIZES = ["md", "lg", "xl", "2xl", "full", "fit"];
const DIALOG_HEIGHTS = ["md", "lg", "xl", "2xl"];
const sizeClasses = {
    md: "sm:s-max-w-md",
    lg: "sm:s-max-w-xl",
    xl: "sm:s-max-w-3xl",
    "2xl": "sm:s-max-w-5xl",
    full: "sm:s-max-w-full sm:s-h-full",
    fit: "sm:s-max-w-[90vw] s-w-fit",
};
const heightClasses = {
    md: "sm:s-h-md",
    lg: "sm:s-h-lg",
    xl: "sm:s-h-xl",
    "2xl": "sm:s-h-2xl",
};
const DIALOG_VARIANTS = ["default", "command"];
const variantClasses = {
    default: cn("s-top-[50%] s-translate-y-[-50%] s-duration-200", "data-[state=open]:s-animate-in data-[state=closed]:s-animate-out", "data-[state=closed]:s-fade-out-0 data-[state=open]:s-fade-in-0", "data-[state=closed]:s-zoom-out-95 data-[state=open]:s-zoom-in-95", "data-[state=closed]:s-slide-out-to-left-1/2 data-[state=closed]:s-slide-out-to-top-[48%]", "data-[state=open]:s-slide-in-from-left-1/2 data-[state=open]:s-slide-in-from-top-[48%]"),
    command: "s-top-[20%]",
};
const dialogVariants = cva(cn("s-fixed s-left-[50%] s-z-50 s-overflow-hidden s-translate-x-[-50%]", "s-rounded-2xl s-flex s-flex-col s-w-full s-max-w-[calc(100vw-2rem)] s-border s-border s-shadow-lg s-sm:rounded-lg", "s-bg-background dark:s-bg-background-night", "s-border-border dark:s-border-border-night", "s-max-h-[90vh]"), {
    variants: {
        size: sizeClasses,
        height: heightClasses,
        variant: variantClasses,
    },
    defaultVariants: {
        size: "md",
        variant: "default",
    },
});
const DialogContent = React.forwardRef(({ className, children, size, height, variant, trapFocusScope, isAlertDialog, preventAutoFocusOnClose = true, onCloseAutoFocus, mountPortalContainer, ...props }, ref) => {
    const handleCloseAutoFocus = React.useCallback((event) => {
        if (preventAutoFocusOnClose) {
            event.preventDefault();
        }
        onCloseAutoFocus === null || onCloseAutoFocus === void 0 ? void 0 : onCloseAutoFocus(event);
    }, [preventAutoFocusOnClose, onCloseAutoFocus]);
    return (React.createElement(DialogPortal, { container: mountPortalContainer },
        React.createElement(DialogOverlay, null),
        React.createElement(FocusScope, { trapped: trapFocusScope, asChild: true },
            React.createElement(DialogPrimitive.Content, { ref: ref, className: cn(dialogVariants({ size, height, variant }), className), onInteractOutside: isAlertDialog
                    ? (e) => e.preventDefault()
                    : props.onInteractOutside, onCloseAutoFocus: handleCloseAutoFocus, ...props }, children))));
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = ({ className, children, buttonSize = "mini", buttonVariant = "ghost", hideButton = false, ...props }) => (React.createElement("div", { className: cn("s-sticky s-top-0 s-z-50 s-flex s-flex-none s-flex-col s-gap-0 s-bg-background s-px-5 s-pt-4 s-text-left dark:s-bg-background-night", className), ...props },
    children,
    !hideButton && (React.createElement(DialogClose, { asChild: true, className: "s-absolute s-right-3 s-top-3" },
        React.createElement(Button, { icon: XClose, variant: buttonVariant, size: buttonSize })))));
DialogHeader.displayName = "DialogHeader";
const DialogContainer = ({ children, fixedContent, className, }) => {
    const contentStyles = cn("s-copy-base s-break-words s-px-5 s-py-4 s-text-foreground dark:s-text-foreground-night");
    const scrollableContent = (React.createElement(ScrollArea, { className: "s-w-full s-flex-grow" },
        React.createElement("div", { className: cn(contentStyles, "s-relative s-flex s-flex-col s-gap-2 s-text-left", className) }, children)));
    if (fixedContent) {
        return (React.createElement("div", { className: "s-flex s-flex-grow s-flex-col s-overflow-hidden" },
            React.createElement("div", { className: cn(contentStyles, "s-flex-none") }, fixedContent),
            React.createElement(Separator, null),
            scrollableContent));
    }
    return scrollableContent;
};
DialogContainer.displayName = "DialogContainer";
const DialogFooter = ({ className, children, leftButtonProps, rightButtonProps, dialogCloseClassName, ...props }) => (React.createElement("div", { className: "s-flex s-flex-none s-flex-col s-gap-0" },
    React.createElement("div", { className: cn("s-flex s-flex-none s-flex-row s-justify-end s-gap-2 s-px-3 s-pb-3 s-pt-2", className), ...props },
        leftButtonProps &&
            (leftButtonProps.disabled ? (React.createElement(Button, { ...leftButtonProps })) : (React.createElement(DialogClose, { className: dialogCloseClassName, asChild: true },
                React.createElement(Button, { ...leftButtonProps })))),
        rightButtonProps &&
            (rightButtonProps.disabled ? (React.createElement(Button, { ...rightButtonProps })) : (React.createElement(DialogClose, { className: dialogCloseClassName, asChild: true },
                React.createElement(Button, { ...rightButtonProps })))),
        children)));
DialogFooter.displayName = "DialogFooter";
const DialogTitle = React.forwardRef(({ className, visual, children, ...props }, ref) => (React.createElement("div", { className: "s-flex s-flex-row s-items-center s-gap-2 s-pt-1" },
    visual,
    React.createElement(DialogPrimitive.Title, { ref: ref, className: cn("s-heading-lg", "s-min-w-0 s-break-words", "s-text-foreground dark:s-text-foreground-night", className), ...props }, children))));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (React.createElement(DialogPrimitive.Description, { ref: ref, className: cn("s-copy-sm", "s-text-muted-foreground dark:s-text-muted-foreground-night", className), ...props })));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
export { Dialog, DialogClose, DialogContainer, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, };
//# sourceMappingURL=Dialog.js.map